import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, X, MessageCircle, Youtube, Instagram, ChevronRight, Home, User, Phone, DollarSign, HelpCircle, ShoppingBag, TrendingUp } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const pendingScrollRef = useRef(null);

  // Detect which section is currently visible
  const detectActiveSection = useCallback(() => {
    const sections = ['how-it-works', 'about', 'calls', 'modules', 'results', 'testimonials', 'pricing', 'faq'];
    for (const section of sections) {
      const el = document.getElementById(section);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 150 && rect.bottom >= 150) {
          return section;
        }
      }
    }
    return '';
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // Skip detection during programmatic scrolling
      if (isScrollingRef.current) return;
      
      const detected = detectActiveSection();
      if (detected) {
        setActiveSection(detected);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [detectActiveSection]);

  // Handle navigation click with two-stage scroll for dynamic content
  const handleNavigation = useCallback((targetId, sectionId) => {
    const element = document.getElementById(targetId);
    if (!element) return;
    
    // Lock scroll detection
    isScrollingRef.current = true;
    pendingScrollRef.current = sectionId;
    
    // Set visual feedback immediately
    setActiveSection(sectionId);
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    const headerOffset = 80;
    
    // Stage 1: Initial scroll to approximate position
    const rect = element.getBoundingClientRect();
    const targetScroll = window.scrollY + rect.top - headerOffset;
    
    window.scrollTo({
      top: Math.max(0, targetScroll),
      behavior: 'smooth'
    });
    
    // Stage 2: After scroll animation, verify and correct position
    const correctPosition = () => {
      const currentRect = element.getBoundingClientRect();
      const offset = currentRect.top - headerOffset;
      
      // If we're not at the right position, scroll more
      if (Math.abs(offset) > 50) {
        window.scrollBy({
          top: offset,
          behavior: 'smooth'
        });
        // Check again after this scroll
        setTimeout(correctPosition, 600);
      } else {
        // We're close enough, done scrolling
        isScrollingRef.current = false;
        pendingScrollRef.current = null;
      }
    };
    
    // Start correction after initial scroll
    setTimeout(correctPosition, 800);
    
    // Fallback timeout
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
      pendingScrollRef.current = null;
    }, 3000);
  }, []);

  const navLinks = [
    { label: 'Как работает', href: '#how-it-works', id: 'how-it-works' },
    { label: 'Автор', href: '#about', id: 'about' },
    { label: 'Созвоны', href: '#calls', id: 'calls' },
    { label: 'Результаты', href: '#results', id: 'results' },
    { label: 'Тарифы', href: '#pricing', id: 'pricing' },
    { label: 'Каталог', href: '/catalog', id: 'catalog', isPage: true },
    { label: 'FAQ', href: '#faq', id: 'faq' },
  ];

  // Bottom nav items for mobile - 6 items
  const bottomNavItems = [
    { icon: Home, label: 'Главная', href: '#how-it-works', id: 'how-it-works' },
    { icon: User, label: 'Автор', href: '#about', id: 'about' },
    { icon: Phone, label: 'Созвоны', href: '#calls', id: 'calls' },
    { icon: TrendingUp, label: 'Результаты', href: '#results', id: 'results' },
    { icon: DollarSign, label: 'Тарифы', href: '#pricing', id: 'pricing' },
    { icon: ShoppingBag, label: 'Каталог', href: '/catalog', id: 'catalog' },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-black/90 backdrop-blur-xl border-b border-zinc-800/50 shadow-lg shadow-black/20' 
          : 'bg-gradient-to-b from-black/50 to-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl blur opacity-40 group-hover:opacity-70 transition-opacity" />
              <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                <span className="text-black font-black text-lg">A</span>
              </div>
            </div>
            <div className="hidden sm:block">
              <span className="text-white font-bold text-lg group-hover:text-amber-400 transition-colors">Amazon</span>
              <span className="text-zinc-400 font-medium text-lg ml-1">Wholesale</span>
            </div>
          </a>

          {/* Desktop Navigation - Pill Style */}
          <nav className="hidden xl:flex items-center">
            <div className="flex items-center gap-1 px-2 py-1.5 rounded-full bg-zinc-900/80 border border-zinc-800/80 backdrop-blur-sm">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => {
                    if (!link.isPage) {
                      e.preventDefault();
                      handleNavigation(link.id, link.id);
                    }
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    activeSection === link.id
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-black shadow-lg shadow-amber-500/20'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </nav>

          {/* Social + CTA */}
          <div className="hidden xl:flex items-center gap-3">
            <div className="flex items-center gap-1.5 p-1 rounded-full bg-zinc-900/80 border border-zinc-800/80">
              <a href="https://t.me/vadjikamazon" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white hover:scale-110 transition-transform shadow-lg shadow-blue-500/30">
                <MessageCircle className="w-4 h-4" />
              </a>
              <a href="https://www.youtube.com/@vadym_ecommerce" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white hover:scale-110 transition-transform shadow-lg shadow-red-500/30">
                <Youtube className="w-4 h-4" />
              </a>
              <a href="https://www.instagram.com/vadym_ecommerce/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-orange-400 flex items-center justify-center text-white hover:scale-110 transition-transform shadow-lg shadow-pink-500/30">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
            <a 
              href="#pricing"
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('pricing', 'pricing');
              }}
              className="group relative h-11 px-6 font-bold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black rounded-full shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all duration-300 flex items-center gap-2 text-sm overflow-hidden"
            >
              <span className="relative z-10">Начать</span>
              <ChevronRight className="w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          {/* Mobile: Social links only */}
          <div className="xl:hidden flex items-center gap-2">
            <a href="https://t.me/vadjikamazon" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-500/20">
              <MessageCircle className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
      
      {/* Mobile Bottom Navigation - Fixed */}
      <div className="xl:hidden fixed bottom-0 left-0 right-0 z-50 bg-zinc-950/95 backdrop-blur-xl border-t border-zinc-800/80 safe-area-pb">
        <div className="flex items-center justify-around h-14 px-1">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            
            return (
              <button
                key={item.id}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Handle external links
                  if (item.href.startsWith('/')) {
                    window.location.href = item.href;
                    return;
                  }
                  
                  // Handle anchor navigation
                  const targetId = item.href.replace('#', '');
                  handleNavigation(targetId, item.id);
                }}
                className={`flex flex-col items-center justify-center flex-1 h-full py-1 transition-colors duration-200 select-none ${
                  isActive ? 'text-amber-400' : 'text-zinc-500'
                }`}
                style={{ 
                  WebkitTapHighlightColor: 'transparent', 
                  touchAction: 'manipulation',
                  WebkitUserSelect: 'none',
                  userSelect: 'none'
                }}
              >
                <Icon className={`w-4 h-4 mb-0.5 pointer-events-none ${isActive ? 'text-amber-400' : 'text-zinc-500'}`} />
                <span className={`text-[9px] font-medium leading-tight pointer-events-none ${isActive ? 'text-amber-400' : 'text-zinc-500'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </header>
  );
};

export default Header;
